import { displayBidderSeatName, isCrossInventoryServerSeat, isSparkForPubPublisherSeat, SCOPE_LEVEL_MAPPING_KEY } from '@pubstack/common/src/bidder-seat'
import { isMarketplaceAlias } from '@pubstack/common/src/bidderCatalog'
import { MEDIA_TYPES } from '@pubstack/common/src/media-types'
import { MediaTypeBadge } from '~/components/MediaTypeBadge'
import { SeatTypeChip } from '../SeatTypeChip'
import { countMappedSites, isScopeLevelSeat } from '../myBidderSeatTabs'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { FunctionComponent, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAdUnits as useAdmAdUnits } from '~/api/adm/ad-units.api'
import { useSitesWithPagination } from '~/api/app/sites.api'
import { BIDDER_SEATS_QUERY_KEYS, useBidderSeat } from '~/api/bidder-seats.api'
import { useCatalogBidder } from '~/api/catalog.api'
import { useAdUnits as usePbsAdUnits } from '~/api/pbs/ad-units.api'
import { Colors } from '~/assets/style/colors'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { CatalogStatus } from '~/components/_styled/catalog'
import { NoDataSpreadsheet } from '~/components/_styled/spreadsheet'
import { Title_H2 } from '~/components/_styled/titles'
import Button from '~/components/Button'
import { Icon } from '~/components/Icon'
import { Link } from '~/components/Link'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { PageContent } from '~/components/PageContent'
import { Skeleton } from '~/components/Skeleton'
import { Tooltip } from '~/components/Tooltip'
import { WidgetMessage } from '~/components/WidgetMessage'
import { ModuleHeader } from '~/feature-components/ModuleHeader'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useProduct } from '~/hooks/useProduct'
import { useScope } from '~/hooks/useScope'
import { PureAdmIntegrationHelpFlyout } from '~/modules/adm/inventory/bidderParams/PureAdmIntegrationHelpFlyout'
import { scopeOwnsOffer } from '~/modules/monetization/upgrade/scopeOffers'
import { configurationState } from '~/state'
import { MARKETPLACE_MAIL } from '~/utils/constants'
import { BidderMapping } from './BidderMapping'
import { MappableAdUnit, mappableAdUnits, mappingSiteFilters } from './mappingInventory'
import { MappingOptionsFlyout } from './MappingOptionsFlyout'
import { canFilterByStackedAdUnits } from './stackedAdUnits'
import { useStackedAdUnitsOnly } from './useStackedAdUnitsOnly'
import { Switch } from '@mantine/core'

export const BidderSeatPage: FunctionComponent = () => {
  // Context
  const { bidderSeatId } = useParams()
  const { STATIC_ASSETS_CDN } = useAtomValue(configurationState)
  const user = useAuthenticatedUser()
  const { canEdit, disabledReason } = useScopePermissions()
  const scopeId = user.currentScopeId
  const isAdmin = user.isAdmin
  const { product } = useProduct()
  const scope = useScope()

  // State
  const [hasUnsavedMappings, setHasUnsavedMappings] = useState(false)

  // Tracking labels
  const BACK_CLICK_TRACKING_LABEL: TrackingLabel = `${product}-bidder-seat-back`
  const FILTER_BY_STACKED_AD_UNITS_TRACKING_LABEL = 'pbs-bidder-seat-mapping-stacked-ad-units-filter-apply'
  const MAPPING_OPTIONS_TRACKING_LABEL: TrackingLabel = `${product}-bidder-seat-mapping-options-edit`
  const MARKETPLACE_MAIL_TRACKING_LABEL: TrackingLabel = `${product}-bidder-seat-send-marketplace-mail-apply`

  // API Hooks
  const { data: bidderSeat, isLoading: isLoadingBidderSeat } = useBidderSeat(product, bidderSeatId ?? '')
  const { data: catalogBidder, isLoading: isLoadingCatalogBidder } = useCatalogBidder(product, bidderSeat?.bidderCode)

  // A `node` seat bids server-side on every media the scope runs, One's included, so it is mapped
  // against both inventories at once — each media offering the ad units its own product holds. Every
  // other seat stays inside its product. See `isCrossInventoryServerSeat`.
  const isCrossInventorySeat = !!bidderSeat && isCrossInventoryServerSeat(bidderSeat)
  // Archived ad units (enabled === false) can't be mapped, so fetch only enabled ones (both products filter server-side).
  // Both hooks are called unconditionally and gated on their `enabled` flag instead, so the hook order
  // never depends on which seat loaded.
  /* Deliberately unfiltered by environment: an ad unit the seat is already mapped on has to reach the
     spreadsheet whatever its env, or the row goes invisible while it keeps bidding, and nothing on this
     page could then remove it. The environments the bidder declares narrow what is *offered* instead —
     see `filteredAdUnits` in `BidderMapping`, which keeps the already-mapped ones. */
  const isPbsAdUnitsEnabled = product === 'pbs'
  // A scope without One has no web inventory to offer, and asking for it would only 403.
  const isAdmAdUnitsEnabled = product === 'adm' || (isCrossInventorySeat && scopeOwnsOffer(scope, 'one'))
  const { data: pbsAdUnits, isLoading: isLoadingPbsAdUnits } = usePbsAdUnits({ enabled: true }, isPbsAdUnitsEnabled)
  const { data: admAdUnits, isLoading: isLoadingAdmAdUnits } = useAdmAdUnits({ enabled: true }, isAdmAdUnitsEnabled)
  // The one place that still knows which collection each ad unit came from, so it is where `product` is stamped.
  // A disabled query keeps answering from the cache, so a list is only taken when its own query was enabled:
  // after a node seat, the One seat would otherwise map over Node's ad units, which carry no `devices`.
  const adUnits = useMemo<MappableAdUnit[]>(
    () => mappableAdUnits({ adUnits: pbsAdUnits, enabled: isPbsAdUnitsEnabled }, { adUnits: admAdUnits, enabled: isAdmAdUnitsEnabled }),
    [pbsAdUnits, admAdUnits, isPbsAdUnitsEnabled, isAdmAdUnitsEnabled]
  )
  const isLoadingAdUnits = isLoadingPbsAdUnits || isLoadingAdmAdUnits
  const {
    data: { pagination },
    isLoading: isLoadingSites,
  } = useSitesWithPagination({
    scopeId,
    // The denominator of "mapped on n/m medias" counts off the same answer the mapping lists from,
    // otherwise a cross-inventory seat reads "mapped on 5/3 medias".
    filters: { ...mappingSiteFilters(bidderSeat, product), search: null },
    pagination: { page: 1, limit: 1 },
    enabled: !!scopeId,
  })

  // Hooks
  // Without the Spark twin's `-int`, matching the row that was clicked to get here.
  const breadcrumbs = useBreadcrumbs({ bidderSeatName: bidderSeat ? displayBidderSeatName(bidderSeat) : '' })
  const navigate = useNavigate()
  const flyout = useGlobalFlyout()
  const [stackedAdUnitsOnly, setStackedAdUnitsOnly] = useStackedAdUnitsOnly()
  // A seat mapped by placement has no ad unit rows to hide, and one not mapped by media has no media
  // whose stacks to read. The preference is stored per user, so it follows them onto both.
  const canFilterAdUnits = bidderSeat ? canFilterByStackedAdUnits(bidderSeat.mappingDimensions) : false
  const queryClient = useQueryClient()

  // Exclude the scope-level sentinel key: it is the Spark team's scope mapping, not a per-site activation.
  const mappedSitesCount = bidderSeat ? countMappedSites(bidderSeat) : 0
  const sitesCount = pagination.total
  const isMarketplace = isMarketplaceAlias(bidderSeat?.name ?? '') && !isAdmin

  // Each half of a Spark For Pub pair now stores the dimensions it is actually mapped by — site×adUnit for
  // the publisher's, placement for the Spark team's — so the page shows the seat as it is rather than
  // forcing one view over a seat that stored the other. The mapping options flyout stays restricted to the
  // Files (CSV isEnabled) tab either way: neither half's dimensions are the customer's to change.
  const isSparkForPubPublisher = !!bidderSeat && isSparkForPubPublisherSeat(bidderSeat)

  // A scope-level seat is mapped by placement and *only* at scope level, so `mappedSitesCount` is
  // structurally zero on one however completely it is mapped. It answers from the sentinel instead, and
  // says plain "Mapped" — there is no media count to put it against.
  const isScopeLevel = !!bidderSeat && isScopeLevelSeat(bidderSeat)
  const isScopeLevelMapped = (bidderSeat?.mappingCountPerSite?.[SCOPE_LEVEL_MAPPING_KEY] ?? 0) > 0

  const handleAdvancedOptionsClick = (initialTab?: 'seat-settings') => {
    if (!bidderSeat) return
    flyout.open(
      MappingOptionsFlyout,
      { color: Colors.Kaiminus, closeTrackingLabel: `${MAPPING_OPTIONS_TRACKING_LABEL}-close` },
      {
        bidderSeat,
        product,
        catalogBidder,
        initialTab,
        onClose: () => {
          flyout.close()
        },
      }
    )
  }

  const handleMappingSaved = () => {
    queryClient.invalidateQueries({ queryKey: [product, ...BIDDER_SEATS_QUERY_KEYS, bidderSeatId] })
  }

  return (
    <PageContent
      breadcrumbs={breadcrumbs}
      headerActions={
        <>
          <Button variant="tertiary" onClick={() => navigate(-1)} trackingLabel={BACK_CLICK_TRACKING_LABEL}>
            Back
          </Button>
          {product === 'adm' && (
            <Button
              variant={'tertiary'}
              onClick={() => flyout.open(PureAdmIntegrationHelpFlyout, { color: Colors.Ming, closeTrackingLabel: 'adm-integration-bidder-help-close' })}
              iconName={'help'}
              trackingLabel={'adm-integration-bidder-help'}
            >
              Help
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <ModuleHeader
          baseCDNUrl={STATIC_ASSETS_CDN}
          catalogItemType="prebidBidder"
          isLoading={isLoadingBidderSeat}
          catalogItemCode={bidderSeat?.bidderCode}
          // The catalog is what turns `appnexus` into Xandr; where it has no entry the code is still the
          // truest name there is, so it stands in rather than leaving the header blank.
          name={catalogBidder?.displayName ?? bidderSeat?.bidderCode}
          // How this seat is integrated, rather than the fixed "Prebid bidder" every seat on this page
          // shares — which of client-side, server-side and Spark it is, is the question the header is read for.
          integrationType={bidderSeat ? <SeatTypeChip seat={bidderSeat} /> : undefined}
          status={
            isScopeLevel ? (
              <CatalogStatus state={isScopeLevelMapped ? 'active' : 'inactive'}>{isScopeLevelMapped ? 'Mapped' : 'Not mapped'}</CatalogStatus>
            ) : (
              <CatalogStatus state={mappedSitesCount ? 'active' : 'inactive'}>
                {mappedSitesCount ? `Mapped on ${mappedSitesCount}/${sitesCount} media${mappedSitesCount > 1 ? 's' : ''}` : 'Not mapped'}
              </CatalogStatus>
            )
          }
          extraHeaderItems={[
            {
              label: 'Media types',
              // Drawn rather than spelled, and the same way the rule and stack flyouts draw them, so the
              // media types a seat can carry are recognised at a glance instead of read as a sentence.
              //
              // An empty catalog entry means "supports everything", which is how the mapping spreadsheet
              // and the bidder rule sections both read it — the header used to answer "N/A" here, which
              // reads as "none" for the one bidder in the list with no entry yet.
              value: (
                <span className={'flex gap-1'}>
                  {(catalogBidder?.supportedMediaTypes?.length ? catalogBidder.supportedMediaTypes : [...MEDIA_TYPES]).map((mediaType) => (
                    <MediaTypeBadge key={mediaType} mediaType={mediaType} variant={'plain'} />
                  ))}
                </span>
              ),
            },
            ...(isSparkForPubPublisher
              ? [
                  {
                    label: 'Mapping',
                    value: <CatalogStatus state={isScopeLevelMapped ? 'active' : 'warning'}>{isScopeLevelMapped ? 'Mapped' : 'Mapping in progress'}</CatalogStatus>,
                  },
                ]
              : []),
            // What the spreadsheet below shows and what "Mapping options" hands over as a CSV, so it sits
            // with the seat's other facts rather than inside the flyout it also narrows. `ml-auto` ends
            // the row with it: it is the one item here that is a control and not a reading.
            ...(canFilterAdUnits
              ? [
                  {
                    label: 'Ad units',
                    className: 'ml-auto',
                    value: (
                      // Not "Deployed": a stack counts here from `to-deploy` onwards, and this app has a
                      // Deployment Center that makes that distinction mean something.
                      <Tooltip title="Show only the ad units this media's stacks use. Server-side ad units always show — they are not tied to a media's stacks." reposition>
                        <Switch
                          checked={stackedAdUnitsOnly}
                          onChange={(event) => setStackedAdUnitsOnly(event.currentTarget.checked)}
                          label="Used in stacks"
                          data-tracking={FILTER_BY_STACKED_AD_UNITS_TRACKING_LABEL}
                        />
                      </Tooltip>
                    ),
                  },
                ]
              : []),
          ]}
        />

        <div className="flex flex-col gap-2 bg-white p-4 [&>div]:rounded">
          <div className="flex items-center justify-between">
            <Title_H2>Bidder mapping</Title_H2>
            {/* The mapping and its options decide what every ad unit on the scope sends this bidder, so both are
                the owner's — and the API refuses them to anyone else. */}
            <Tooltip title={canEdit ? 'You need to save your mappings to edit mapping options.' : disabledReason} disabled={canEdit && !hasUnsavedMappings} reposition>
              <Button variant="tertiary" iconName="settings" disabled={hasUnsavedMappings || !canEdit} trackingLabel={MAPPING_OPTIONS_TRACKING_LABEL} onClick={() => handleAdvancedOptionsClick()}>
                Mapping options
              </Button>
            </Tooltip>
          </div>
          {isMarketplace && (
            <WidgetMessage
              message={
                <div>
                  This is a Marketplace integration. Mapping and bid adjustment are managed by Pubstack. Please contact{' '}
                  <Link label={MARKETPLACE_MAIL} href={`mailto:${MARKETPLACE_MAIL}`} trackingLabel={MARKETPLACE_MAIL_TRACKING_LABEL} variant="secondary" /> if you spot an issue or have questions.
                </div>
              }
              icon={'tips'}
            />
          )}
          {isLoadingBidderSeat || isLoadingCatalogBidder || isLoadingAdUnits || isLoadingSites ? (
            <Skeleton bigger />
          ) : !bidderSeat || !catalogBidder ? (
            <NoDataSpreadsheet>
              <Icon name="night_sky" width="144px" />
              <div>Something went wrong while loading data</div>
            </NoDataSpreadsheet>
          ) : (
            <BidderMapping
              bidderSeat={bidderSeat}
              catalogBidder={catalogBidder}
              adUnits={adUnits}
              platform={bidderSeat.platform ?? 'server'}
              product={product}
              onSaveSuccess={handleMappingSaved}
              disabled={isMarketplace || !canEdit}
              onUnsavedMappingsChange={setHasUnsavedMappings}
              onConfigureObjectParams={canEdit ? () => handleAdvancedOptionsClick('seat-settings') : undefined}
            />
          )}
        </div>
      </div>
    </PageContent>
  )
}
